import { useEffect, useState } from "react";
import { api } from "../api/client";
import type { StatusInfo } from "../api/types";
import { useToast, errText } from "../components/ui";
import { IconDatabase, IconActivity, IconWallet, IconShield, IconRefresh } from "../components/icons";

export default function Overview({ onGo }: { onGo: (id: string) => void }) {
  const notify = useToast();
  const [st, setSt] = useState<StatusInfo | null>(null);
  const [counts, setCounts] = useState({ sources: 0, strategies: 0, traders: 0, enabledSources: 0, enabledStrategies: 0 });

  const load = async () => {
    try {
      const [status, ds, ss, tr] = await Promise.all([
        api.status(), api.listDataSources(), api.listStrategies(), api.listTraders(),
      ]);
      setSt(status);
      setCounts({
        sources: ds.length, strategies: ss.length, traders: tr.length,
        enabledSources: ds.filter((x) => x.enabled).length,
        enabledStrategies: ss.filter((x) => x.enabled).length,
      });
    } catch (e) { notify({ kind: "error", msg: errText(e) }); }
  };
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, []);

  const runningSources = st?.data_sources?.length ?? 0;
  const runningStrategies = st?.strategies?.length ?? 0;
  const dropped = (st?.engine?.dropped_total as number | undefined) ?? null;
  const halted = st?.risk?.halted ?? null;

  return (
    <div>
      <div className="statgrid">
        <Stat icon={<IconDatabase />} k="数据源" v={counts.sources} sub={`${counts.enabledSources} 启用 · ${runningSources} 运行中`} onClick={() => onGo("sources")} />
        <Stat icon={<IconActivity />} k="策略" v={counts.strategies} sub={`${counts.enabledStrategies} 启用 · ${runningStrategies} 运行中`} onClick={() => onGo("strategies")} />
        <Stat icon={<IconWallet />} k="执行器" v={counts.traders} sub="预设交易通道" onClick={() => onGo("traders")} />
        <Stat
          icon={<IconShield />} k="组合风控"
          v={halted === null ? "—" : halted ? "熔断" : "正常"}
          sub={st?.risk ? `日内已实现 ${fmt(st.risk.realized_pnl)}` : "未启用"}
          tone={halted ? "short" : halted === false ? "long" : undefined}
          onClick={() => onGo("risk")}
        />
      </div>

      <div className="panel">
        <div className="panel-head">
          <span className="title">运行状态</span>
          <span className="spacer" />
          <button className="ghost sm" onClick={load}><IconRefresh size={14} /> 刷新</button>
        </div>
        {!st ? <div className="muted">加载中…</div> : (
          <div className="grid2">
            <div>
              <label>运行中数据源</label>
              <div>{(st.data_sources || []).length ? (st.data_sources || []).map((s) => <span key={s} className="badge kind mono" style={{ marginRight: 6 }}>{s}</span>) : <span className="muted">— 引擎未接入或无运行实例</span>}</div>
            </div>
            <div>
              <label>运行中策略</label>
              <div>{(st.strategies || []).length ? (st.strategies || []).map((s) => <span key={s} className="badge mono" style={{ marginRight: 6 }}>{s}</span>) : <span className="muted">—</span>}</div>
            </div>
          </div>
        )}
      </div>

      {st?.engine && (
        <div className="panel">
          <div className="panel-head"><span className="title">引擎指标</span>{dropped ? <span className="badge err">丢弃 {dropped}</span> : <span className="badge on">无丢弃</span>}</div>
          <div className="statgrid">
            {Object.entries(st.engine).map(([k, v]) => (
              <div className="stat" key={k}><div className="k mono">{k}</div><div className="v tnum" style={{ fontSize: 20 }}>{String(v)}</div></div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ icon, k, v, sub, onClick, tone }: {
  icon: JSX.Element; k: string; v: string | number; sub?: string; onClick?: () => void; tone?: "long" | "short";
}) {
  return (
    <div className="stat" onClick={onClick} style={{ cursor: onClick ? "pointer" : "default" }}>
      <div className="glow" />
      <div className="k">{icon}{k}</div>
      <div className={`v ${tone || ""}`}>{v}</div>
      {sub && <div className="sub">{sub}</div>}
    </div>
  );
}

function fmt(n: number | null | undefined): string {
  if (n === null || n === undefined) return "0";
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}
